import { getSport } from "@/sports";

export const uid = (p = "id") => `${p}-${Math.random().toString(36).slice(2, 10)}`;
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

export function lerpPath(pts, f) {
  if (pts.length < 2) return pts[0];
  const segs = pts.slice(1).map((p, i) => dist(pts[i], p));
  const total = segs.reduce((a, b) => a + b, 0);
  if (!total) return pts[0];
  let left = f * total;
  for (let i = 0; i < segs.length; i++) {
    if (left <= segs[i]) {
      const r = segs[i] ? left / segs[i] : 0;
      return { x: pts[i].x + (pts[i + 1].x - pts[i].x) * r, y: pts[i].y + (pts[i + 1].y - pts[i].y) * r };
    }
    left -= segs[i];
  }
  return pts[pts.length - 1];
}

export const pathLength = (pts) => pts.slice(1).reduce((s, p, i) => s + dist(pts[i], p), 0);
export const playerById = (play, id) => play.players.find((p) => p.id === id);
export const playerRoutes = (play, pid) =>
  play.routes.filter((r) => r.player_id === pid).sort((a, b) => a.start - b.start);

export function routePath(play, route) {
  const pl = playerById(play, route.player_id);
  let pos = { x: pl.x, y: pl.y };
  for (const r of playerRoutes(play, route.player_id)) {
    if (r.id === route.id) return [pos, ...r.points];
    if (r.points.length) pos = r.points[r.points.length - 1];
  }
  return [pos, ...route.points];
}

export function playerPosAt(play, player, t) {
  let pos = { x: player.x, y: player.y };
  for (const r of playerRoutes(play, player.id)) {
    if (t < r.start) return pos;
    const { path, legs } = routeTiming(play, r);
    for (const leg of legs) {
      if (t < leg.t1) return lerpPath(leg.pts, leg.t1 > leg.t0 ? (t - leg.t0) / (leg.t1 - leg.t0) : 1);
      if (t < leg.tEnd) return leg.pts[leg.pts.length - 1];
    }
    pos = path[path.length - 1];
  }
  return pos;
}

function catmull(p0, p1, p2, p3, t) {
  const t2 = t * t, t3 = t2 * t;
  const f = (a, b, c, d) => 0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
  return { x: f(p0.x, p1.x, p2.x, p3.x), y: f(p0.y, p1.y, p2.y, p3.y) };
}

export function segmentSamples(path, curve) {
  return path.slice(1).map((b, i) => {
    const a = path[i];
    if (curve !== "smooth" || path.length < 3) return [a, b];
    const p0 = path[i - 1] || a, p3 = path[i + 2] || b;
    return Array.from({ length: 13 }, (_, k) => catmull(p0, a, b, p3, k / 12));
  });
}

// Consistent speed across free segments; per-segment `dur` overrides and waypoint `pause`s respected.
export function routeTiming(play, route) {
  const path = routePath(play, route);
  const samples = segmentSamples(path, route.curve);
  const lens = samples.map(pathLength);
  const over = route.points.map((p) => (p.dur > 0 ? p.dur : null));
  const overSum = over.reduce((s, v) => s + (v || 0), 0);
  const freeLen = lens.reduce((s, l, i) => s + (over[i] == null ? l : 0), 0);
  const freeTime = Math.max(0.05, route.duration - overSum);
  let t = route.start;
  const legs = samples.map((pts, i) => {
    const d = over[i] ?? (freeLen ? (lens[i] / freeLen) * freeTime : 0);
    const leg = { pts, len: lens[i], t0: t, t1: t + d, tEnd: t + d + (route.points[i].pause || 0) };
    t = leg.tEnd;
    return leg;
  });
  return { path, samples, legs, end: t };
}

export const routeEnd = (play, r) => routeTiming(play, r).end;
export const routeLength = (play, r) => routeTiming(play, r).legs.reduce((s, l) => s + l.len, 0);

export const sortedEvents = (play) => [...(play.ball?.events || [])].sort((a, b) => a.time - b.time);

export function ballAt(play, t) {
  let holder = play.ball?.carrier;
  for (const e of sortedEvents(play)) {
    if (t < e.time) break;
    const end = e.time + e.duration;
    const from = playerById(play, e.from_id), to = playerById(play, e.to_id);
    if (!from || !to) continue;
    if (t < end) {
      const a = playerPosAt(play, from, e.time), b = playerPosAt(play, to, end);
      const f = (t - e.time) / e.duration;
      return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, lift: e.type === "pass" ? Math.sin(Math.PI * f) : 0, holder: null };
    }
    holder = e.to_id;
  }
  const p = holder && playerById(play, holder);
  if (!p) return null;
  const pos = playerPosAt(play, p, t);
  return { x: pos.x + 1.4, y: pos.y - 0.9, lift: 0, holder };
}

export function snapTime(play) {
  const s = sortedEvents(play).find((e) => e.type === "snap");
  return s ? s.time : 0;
}

export function totalDuration(play) {
  const ends = [1, ...play.routes.map((r) => routeEnd(play, r)), ...(play.ball?.events || []).map((e) => e.time + e.duration), ...(play.phases || []).map((p) => p.time)];
  return Math.round((Math.max(...ends) + 0.5) * 100) / 100;
}

export const phasesSorted = (play) => [...(play.phases || [])].sort((a, b) => a.time - b.time);

export function phaseAt(play, t) {
  let cur = null;
  for (const ph of phasesSorted(play)) if (ph.time <= t + 0.001) cur = ph;
  return cur;
}

export function playerWindow(play, pid) {
  const rs = playerRoutes(play, pid);
  const evs = sortedEvents(play).filter((e) => e.from_id === pid || e.to_id === pid);
  const starts = [...rs.map((r) => r.start), ...evs.map((e) => e.time)];
  const ends = [...rs.map((r) => routeEnd(play, r)), ...evs.map((e) => e.time + e.duration)];
  if (!starts.length) return { start: 0, end: totalDuration(play) };
  return { start: Math.max(0, Math.min(...starts) - 0.6), end: Math.max(...ends) + 0.4 };
}

// ---------- Kid-friendly auto instructions ----------
const n1 = (v) => Math.max(1, Math.round(Math.abs(v)));
const yds = (v) => `${n1(v)} yard${n1(v) > 1 ? "s" : ""}`;

function dirWords(dx, dy, side) {
  const fwd = side === "offense" ? -dy : dy;
  const right = side === "offense" ? dx : -dx;
  const parts = [];
  if (Math.abs(fwd) >= 0.8) parts.push(`${yds(fwd)} ${fwd > 0 ? "forward" : "back"}`);
  if (Math.abs(right) >= 0.8) parts.push(`${yds(right)} to your ${right > 0 ? "right" : "left"}`);
  return parts.join(" and ") || "a short step";
}

export function autoLineup(play, p) {
  const cx = getSport(play.sport).centerX;
  const behind = p.side === "offense" ? p.y - play.los_y : play.los_y - p.y;
  const dx = p.x - cx;
  const right = p.side === "offense" ? dx : -dx;
  const depth = behind < 1.6 ? "right at the line of scrimmage" : `about ${yds(behind)} ${p.side === "offense" ? "behind" : "back from"} the line`;
  const side = Math.abs(dx) < 1.2 ? "lined up with the ball" : `${yds(dx)} to the ${right > 0 ? "right" : "left"} of the ball`;
  return `Line up ${depth}, ${side}.`;
}

export function autoBeforeSnap(play, p) {
  const snap = snapTime(play);
  if (playerRoutes(play, p.id).some((r) => r.start < snap - 0.05)) return "Go in motion before the snap, then keep going.";
  return p.side === "offense" ? "Get in your stance. Stay still until the ball is snapped." : "Get in your stance and watch the ball.";
}

export function autoWhen(play, p) {
  const rs = playerRoutes(play, p.id);
  if (!rs.length) return "Stay in your spot after the snap.";
  const snap = snapTime(play), d = rs[0].start - snap;
  if (rs[0].start < snap - 0.05) return "Start moving before the snap. That is your motion.";
  if (d <= 0.2) return "Start moving as soon as the ball is snapped.";
  return `Count to ${Math.max(1, Math.round(d))} after the snap, then go.`;
}

const VERB = { run: "Run", block: "Block:", motion: "Go in motion" };
const GIVE = { snap: "Snap the ball to", handoff: "Hand the ball to", pitch: "Pitch the ball to", pass: "Throw the ball to" };
const GET = { snap: "Catch the snap from", handoff: "Take the handoff from", pitch: "Catch the pitch from", pass: "Catch the pass from" };

export function ballText(play, p, from = 0, to = Infinity) {
  return sortedEvents(play).filter((e) => e.time >= from - 0.001 && e.time < to).map((e) => {
    if (e.from_id === p.id) return `${GIVE[e.type]} ${playerById(play, e.to_id)?.label}.`;
    if (e.to_id === p.id) return `${GET[e.type]} ${playerById(play, e.from_id)?.label}.`;
    return null;
  }).filter(Boolean).join(" ");
}

function routeText(play, p, r) {
  const { path } = routeTiming(play, r);
  const legs = path.slice(1).map((b, i) => dirWords(b.x - path[i].x, b.y - path[i].y, p.side));
  const pauses = r.points.some((pt) => pt.pause > 0) ? " Stop for a moment where your coach marked a pause." : "";
  const words = legs.join(", then cut ");
  return (r.type === "block" ? `Block: step ${words} and block the player in front of you.` : `${VERB[r.type]} ${words}.`) + pauses;
}

export function autoWhere(play, p, from = 0, to = Infinity) {
  const moves = playerRoutes(play, p.id).filter((r) => r.start < to && routeEnd(play, r) > from).map((r) => routeText(play, p, r));
  const txt = [moves.join(" Then "), ballText(play, p, from, to)].filter(Boolean).join(" ");
  return txt || "Hold your spot.";
}

export function jobFor(play, p) {
  return {
    lineup: p.lineup || autoLineup(play, p),
    before: p.before_snap || autoBeforeSnap(play, p),
    when: p.when_move || autoWhen(play, p),
    where: p.where_move || autoWhere(play, p),
    instruction: p.instruction || "",
  };
}

export function phaseNoteFor(play, p, idx) {
  const phases = phasesSorted(play);
  const ph = phases[idx];
  if (!ph) return "";
  if (p.phase_notes?.[ph.id]) return p.phase_notes[ph.id];
  if (idx === 0) return `${autoLineup(play, p)} ${autoBeforeSnap(play, p)}`;
  const next = phases[idx + 1]?.time ?? Infinity;
  return autoWhere(play, p, ph.time, next);
}
